import type { RemoteCursorMessage } from '../shared/datachannel';
import type { PointerInputState, Point } from './pointer-types';
import { createOptimisticCursor, genericCursorCss } from './pointer-types';

export interface CursorLayer {
  hideCursor(): void;
  showCursorAt(point: Point): void;
  refresh(): void;
  setRemoteCursor(message: RemoteCursorMessage): void;
  setRemoteCursorMode(mode: 'captured' | 'overlay'): void;
  clearRemoteCursor(): void;
  dispose(): void;
}

// 远端光标渲染子系统：本机原生 CSS 光标跟随鼠标，不经过 WebRTC；
// 叠加的 DOM 光标不再参与显示，避免与系统指针互相遮挡或因缩放漂移。
export function createCursorLayer(el: HTMLVideoElement, state: PointerInputState): CursorLayer {
  let remoteCursor: RemoteCursorMessage | null = null;
  let remoteCursorHidden = false;
  let lastPoint: Point | null = null;
  const cursorParent = el.parentElement;
  const optimisticCursor = cursorParent ? createOptimisticCursor(cursorParent) : null;
  const cursorImage = optimisticCursor?.querySelector<HTMLImageElement>('.cursor-image') || null;

  const setNativeCursor = (value: string): void => {
    // CSS cursor 是浏览器原生绘制的，位置跟随本机指针，不经过 WebRTC 或
    // DOM 重排。控制区禁止使用 `cursor: none`：即使远端光标采集或模式
    // 判断失效，本机指针也必须始终可见。
    el.style.cursor = value === 'none' ? 'default' : value;
  };

  const showGenericNativeCursor = (): void => {
    setNativeCursor(genericCursorCss(state.getRemotePlatform?.() || 'unknown'));
  };

  const showRemoteNativeCursor = (message: RemoteCursorMessage): void => {
    if (!message.data) {
      showGenericNativeCursor();
      return;
    }
    const hotspotX = Math.max(0, Math.min(Math.round(message.hotspotX || 0), Math.max(0, (message.width || 1) - 1)));
    const hotspotY = Math.max(0, Math.min(Math.round(message.hotspotY || 0), Math.max(0, (message.height || 1) - 1)));
    const fallback = genericCursorCss(state.getRemotePlatform?.() || 'unknown');
    setNativeCursor(`url("${message.data}") ${hotspotX} ${hotspotY}, ${fallback}`);
  };

  const hideCursor = (): void => {
    optimisticCursor?.classList.remove('is-visible');
  };

  const showCursor = (point: Point): void => {
    lastPoint = point;
    // 本机原生 CSS 光标直接跟随鼠标，完全不走 WebRTC，也不等待远端回包。
    // 远端能提供真实形状时使用裁剪后的 PNG；否则使用目标平台近似指针。
    // DOM 叠加光标不再参与显示，避免它与系统指针互相遮挡或因坐标缩放漂移。
    hideCursor();
    if (remoteCursor?.data && !remoteCursorHidden) showRemoteNativeCursor(remoteCursor);
    else showGenericNativeCursor();
  };

  const setRemoteCursor = (message: RemoteCursorMessage): void => {
    if (!optimisticCursor) return;
    if (!message.visible) {
      remoteCursor = null;
      // 没有独立图形时始终保留本机可见的平台光标，避免视频光标和备用层
      // 同时不可见。
      remoteCursorHidden = false;
      optimisticCursor.dataset.cursor = 'hidden';
      optimisticCursor.classList.remove('is-visible');
      cursorImage?.removeAttribute('src');
      showGenericNativeCursor();
      return;
    }
    if (!message.data) return;

    remoteCursorHidden = false;
    remoteCursor = {
      ...message,
      width: Math.max(1, message.width || 64),
      height: Math.max(1, message.height || 64),
      hotspotX: Math.max(0, message.hotspotX || 0),
      hotspotY: Math.max(0, message.hotspotY || 0),
      sourceWidth: Math.max(1, message.sourceWidth || el.videoWidth || 1),
      sourceHeight: Math.max(1, message.sourceHeight || el.videoHeight || 1),
    };
    showRemoteNativeCursor(remoteCursor);
    optimisticCursor.dataset.cursor = 'remote';
    if (cursorImage) {
      cursorImage.onload = () => {
        if (lastPoint) showCursor(lastPoint);
      };
      cursorImage.onerror = () => {
        remoteCursor = null;
        optimisticCursor.dataset.cursor = 'generic';
        if (lastPoint) showCursor(lastPoint);
      };
      cursorImage.src = message.data;
    }
    if (lastPoint) showCursor(lastPoint);
  };

  const setRemoteCursorMode = (mode: 'captured' | 'overlay'): void => {
    // captured 仅表示移动端把光标编码进视频；控制端不再对视频做局部截图
    // 探测，也不隐藏本机指针。PC 端由 HostSession 提供原生光标图形。
    void mode;
    remoteCursorHidden = false;
    showGenericNativeCursor();
    if (lastPoint) showCursor(lastPoint);
  };

  const clearRemoteCursor = (): void => {
    remoteCursor = null;
    remoteCursorHidden = false;
    if (optimisticCursor) optimisticCursor.dataset.cursor = 'generic';
    cursorImage?.removeAttribute('src');
    setNativeCursor('default');
  };

  const dispose = (): void => {
    clearRemoteCursor();
    optimisticCursor?.remove();
  };

  return {
    hideCursor,
    showCursorAt: showCursor,
    refresh: () => { if (lastPoint) showCursor(lastPoint); },
    setRemoteCursor,
    setRemoteCursorMode,
    clearRemoteCursor,
    dispose,
  };
}
